import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useNavigate, useParams } from "react-router-dom";
import Button from "../../Generic/Button";
import Card from "../../Generic/Card";
import Dropdown from "../../Generic/Dropdown";
import InputBox from "../../Generic/InputBox";
import SuccessButton from "../../Generic/SuccessButton";
import Textarea from "../../Generic/Textarea";
import { errorNotification, successNotification } from "../../Generic/Notifications";
import StudioService from "../Services/StudioService";
import { AppRoutes, DashboardModules, StudioSubModules } from "../../config/app.config";
import {
	DISH_DESCRIPTION_MAX_LENGTH,
	DISH_TITLE_MAX_LENGTH,
} from "./constants";
import { useDish } from "./context/DishContext";
import { Cross } from "../../Generic/Symbols";
import { buildDishTagDropdownOptions, dishTagLabel, isDishTag, validateDishForm } from "./utils";
import { isEmpty } from "../../util/util";
import { zIndex } from "../../config/zIndex";

const Page = styled.div`
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 1.5rem;
`;

const DishEditCard = styled(Card)`
	width: 100%;
	align-items: center;
	flex-direction: column;
`;

const DishEditCardHeader = styled.div`
	display: flex;
	align-items: center;
	width: 100%;
	padding-bottom: 1.5rem;
	gap: 1.5rem;
`;

const DishEditCardHeaderTitle = styled.div`
	font-size: 2.3rem;
	font-weight: 600;
	color: #000;
`;

const DishEditCardHeaderBackButton = styled.span.attrs({
	className: "material-symbols-outlined",
})`
	cursor: pointer;
	color: #000;
`;

const DishEditForm = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1.5rem;
	width: 100%;
	align-items: center;
`;

const DishEditFormItem = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
	width: 100%;
`;

const DishEditFormItemLabel = styled.div`
	font-size: 1rem;
	font-weight: 600;
	color: #000;
`;

const DishEditFormItemInput = styled(InputBox)`
	width: 30rem;
`;

const DishEditFormItemTextarea = styled(Textarea)`
	width: 100%;
`;

const DishEditFormFooter = styled.div`
	display: flex;
	justify-content: flex-end;
	gap: 1rem;
	padding: 1rem 0;
`;

const DishEditFormItemTags = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	justify-content: flex-start;
	width: 100%;
`;

const DishEditFormItemTag = styled.div`
	position: relative;
	margin: 0 0.5rem 0 0;
	padding: 0.5rem 0 0 0;
`;

const DishEditFormItemTagRemove = styled.div`
	position: absolute;
	top: 0;
	right: 0%;
	z-index: ${zIndex.stickyHeader};
	cursor: pointer;
`;

const DishEditFormItemTagPill = styled.div`
	background: rgba(45, 35, 66, 0.8);
	border-radius: 3px 0 0 3px;
	color: #fff;
	display: inline-block;
	height: 26px;
	line-height: 26px;
	padding: 0 20px 0 23px;
	position: relative;
	margin: 0 10px 10px 0;
`;

const DishEditFormItemTagInputRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 0.5rem;
	width: 100%;
	margin-top: 0.25rem;
`;

const DishTagDropdownWrap = styled.div`
	flex: 1;
	min-width: 12rem;
	max-width: 24rem;
`;

const DishEdit = () => {
	const { id: dishId } = useParams();
	const navigate = useNavigate();
	const { editDishDraft, editDraftDishId, updateEditDishDraft, resetEditDishDraft, invalidateDishListCache } = useDish();

	const [isDishEditSubmitting, setIsDishEditSubmitting] = useState(false);
	const [tagPickerKey, setTagPickerKey] = useState(0);

	const detailsPath = AppRoutes[DashboardModules.STUDIO][StudioSubModules.DISH].DISH_DETAILS.getPath(dishId);
	const draft = editDraftDishId === dishId ? editDishDraft : {};
	const draftTags = useMemo(() => {
		const raw = Array.isArray(draft.tags) ? draft.tags : [];
		return [...new Set(raw.filter(isDishTag))];
	}, [draft.tags]);
	const tagDropdownOptions = useMemo(() => buildDishTagDropdownOptions(draftTags), [draftTags]);

	useEffect(() => {
		if(isEmpty(draft) || editDraftDishId !== dishId) {
			navigate(detailsPath);
		}
	}, [draft, editDraftDishId, dishId, detailsPath, navigate]);

	function handleDishTextFieldChange(fieldName, value) {
		if (fieldName === "title") {
			updateEditDishDraft({ title: value });
		} else if (fieldName === "description") {
			updateEditDishDraft({ description: value });
		} else if (fieldName === "notes") {
			updateEditDishDraft({ notes: value });
		} else if (fieldName === "imageUrl") {
			updateEditDishDraft({ imageUrl: value });
		}
	}

	function handleDishTagRemove(tagToRemove) {
		updateEditDishDraft({
			tags: draftTags.filter((t) => t !== tagToRemove),
		});
	}

	function handleTagDropdownPick(key) {
		if (!key || !isDishTag(key) || draftTags.includes(key)) {
			return;
		}
		updateEditDishDraft({
			tags: [...draftTags, key],
		});
		setTagPickerKey((k) => k + 1);
	}

	async function submitUpdatedDish() {
		try {
			validateDishForm({
				title: draft?.title,
				description: draft?.description,
				notes: draft?.notes,
				imageUrl: draft?.imageUrl,
				tags: draftTags,
			});
		} catch (error) {
			errorNotification(error.message);
			return;
		}
		setIsDishEditSubmitting(true);
		const requestPayload = {
			title: draft.title.trim(),
			description: draft.description.trim(),
			notes: draft.notes.trim(),
			imageUrl: draft.imageUrl.trim(),
			tags: draftTags,
		};
		const response = await StudioService.updateDish(dishId, requestPayload);
		const { data, error } = response;
		setIsDishEditSubmitting(false);
		if (error) {
			errorNotification("Could not save changes");
			return;
		}
		if (data) {
			successNotification("Changes saved successfully");
			resetEditDishDraft();
			invalidateDishListCache();
			navigate(detailsPath, { replace: true });
		}
	}

	function handleNavigateToDishDetails() {
		navigate(detailsPath);
	}

	return (
		<Page>
			<DishEditCardHeader>
				<DishEditCardHeaderBackButton disabled={isDishEditSubmitting} onClick={handleNavigateToDishDetails}>arrow_back</DishEditCardHeaderBackButton>
				<DishEditCardHeaderTitle>Edit Dish</DishEditCardHeaderTitle>
			</DishEditCardHeader>
			<DishEditCard>
				<DishEditForm>
					<DishEditFormItem>
						<DishEditFormItemLabel>Name</DishEditFormItemLabel>
						<DishEditFormItemInput
							maxLength={DISH_TITLE_MAX_LENGTH}
							value={draft.title}
							onChange={(event) => handleDishTextFieldChange("title", event.target.value)}
						/>
					</DishEditFormItem>
					<DishEditFormItem>
						<DishEditFormItemLabel>Tags (at least one)</DishEditFormItemLabel>
						<DishEditFormItemTags>
							{draftTags.map((tag, index) => (
								<DishEditFormItemTag key={`${tag}-${index}`}>
									<DishEditFormItemTagRemove onClick={() => handleDishTagRemove(tag)} aria-label={`Remove tag ${dishTagLabel(tag)}`}>
										<Cross width="15px" />
									</DishEditFormItemTagRemove>
									<DishEditFormItemTagPill id={tag}>{dishTagLabel(tag)}</DishEditFormItemTagPill>
								</DishEditFormItemTag>
							))}
						</DishEditFormItemTags>
						<DishEditFormItemTagInputRow>
							{tagDropdownOptions.length > 0 ? (
								<DishTagDropdownWrap>
									<Dropdown
										key={tagPickerKey}
										head="Add tag…"
										value={undefined}
										optionsList={tagDropdownOptions}
										onChange={handleTagDropdownPick}
										padding="0.5rem"
										headerFontSize="1rem"
										boxShadow=""
										border="1px solid rgba(0,0,0,0.6)"
										margin="0"
										widthVal="100%"
									/>
								</DishTagDropdownWrap>
							) : (
								<span style={{ color: "rgba(0,0,0,0.55)", fontSize: "0.95rem" }}>All tags added</span>
							)}
						</DishEditFormItemTagInputRow>
					</DishEditFormItem>
					<DishEditFormItem>
						<DishEditFormItemLabel>Description (max {DISH_DESCRIPTION_MAX_LENGTH} characters)</DishEditFormItemLabel>
						<DishEditFormItemTextarea
							maxLength={DISH_DESCRIPTION_MAX_LENGTH}
							height="8rem"
							value={draft.description}
							onChange={(event) => handleDishTextFieldChange("description", event.target.value)}
						/>
					</DishEditFormItem>
					<DishEditFormItem>
						<DishEditFormItemLabel>Notes</DishEditFormItemLabel>
						<DishEditFormItemTextarea value={draft.notes} onChange={(event) => handleDishTextFieldChange("notes", event.target.value)} />
					</DishEditFormItem>
					<DishEditFormItem>
						<DishEditFormItemLabel>Image URL</DishEditFormItemLabel>
						<DishEditFormItemInput value={draft.imageUrl} onChange={(event) => handleDishTextFieldChange("imageUrl", event.target.value)} />
					</DishEditFormItem>
				</DishEditForm>
				<DishEditFormFooter>
					<Button onClick={handleNavigateToDishDetails}>Cancel</Button>
					<SuccessButton onClick={submitUpdatedDish} disabled={isDishEditSubmitting}>
						{isDishEditSubmitting ? "Saving…" : "Save changes"}
					</SuccessButton>
				</DishEditFormFooter>
			</DishEditCard>
		</Page>
	);
};

export default DishEdit;
